import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, X, Upload } from "lucide-react";

// Field schemas per page slug. Each entry: { key, label, type, full? }
// type: "text" | "textarea" | "image" | "list:faq" | "list:values" | "list:stats" | "list:subjects" | "list:sections" | "robots"
const SEO_FIELDS = [
  { key: "__seo_header__", label: "Search engine listing (SEO)", type: "section_header", full: true },
  { key: "seo_title", label: "Meta title (50–60 chars recommended)", type: "text", full: true },
  { key: "seo_description", label: "Meta description (140–160 chars)", type: "textarea", full: true },
  { key: "seo_keywords", label: "Keywords (comma separated)", type: "text", full: true },
  { key: "og_image", label: "Open Graph image URL (social previews)", type: "image", full: true },
  { key: "seo_robots", label: "Search engines indexing", type: "robots", full: true },
];

const SCHEMAS = {
  about: [
    { key: "title", label: "Page title", type: "text", full: true },
    { key: "hero_eyebrow", label: "Hero eyebrow", type: "text" },
    { key: "hero_title", label: "Hero title", type: "text", full: true },
    { key: "hero_subtitle", label: "Hero subtitle", type: "textarea", full: true },
    { key: "hero_image", label: "Hero image", type: "image", full: true },
    { key: "story_title", label: "Story — title", type: "text", full: true },
    { key: "story_body", label: "Story — body", type: "textarea", full: true },
    { key: "story_image_1", label: "Story image 1", type: "image" },
    { key: "story_image_2", label: "Story image 2", type: "image" },
    { key: "mission_title", label: "Mission title", type: "text" },
    { key: "mission_body", label: "Mission body", type: "textarea", full: true },
    { key: "values", label: "Values", type: "list:values", full: true },
    { key: "founder_name", label: "Founder name", type: "text" },
    { key: "founder_role", label: "Founder role", type: "text" },
    { key: "founder_quote", label: "Founder quote", type: "textarea", full: true },
    { key: "founder_image", label: "Founder image", type: "image", full: true },
    { key: "stats", label: "Stats", type: "list:stats", full: true },
  ],
  contact: [
    { key: "title", label: "Page title", type: "text", full: true },
    { key: "hero_eyebrow", label: "Hero eyebrow", type: "text" },
    { key: "hero_title", label: "Hero title", type: "text", full: true },
    { key: "hero_subtitle", label: "Hero subtitle", type: "textarea", full: true },
    { key: "email", label: "Email", type: "text" },
    { key: "phone", label: "Phone", type: "text" },
    { key: "whatsapp", label: "WhatsApp", type: "text" },
    { key: "address", label: "Address", type: "textarea", full: true },
    { key: "hours", label: "Hours", type: "text" },
    { key: "instagram", label: "Instagram URL", type: "text" },
    { key: "map_embed", label: "Google Maps embed URL", type: "text", full: true },
    { key: "subjects", label: "Form subject options", type: "list:subjects", full: true },
    { key: "faqs", label: "FAQs", type: "list:faq", full: true },
  ],
  terms: [
    { key: "title", label: "Page title", type: "text", full: true },
    { key: "intro", label: "Intro", type: "textarea", full: true },
    { key: "sections", label: "Sections", type: "list:sections", full: true },
  ],
  privacy: [
    { key: "title", label: "Page title", type: "text", full: true },
    { key: "intro", label: "Intro", type: "textarea", full: true },
    { key: "sections", label: "Sections", type: "list:sections", full: true },
  ],
  returns: [
    { key: "title", label: "Page title", type: "text", full: true },
    { key: "intro", label: "Intro", type: "textarea", full: true },
    { key: "sections", label: "Sections", type: "list:sections", full: true },
  ],
};

const PAGE_TABS = [
  { slug: "about", label: "About" },
  { slug: "contact", label: "Contact" },
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy" },
  { slug: "returns", label: "Returns" },
];

const inputDark = "w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-cream placeholder:text-cream/60 focus:border-[#C9789A] focus:outline-none transition";

const ImageField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cloudinary not configured — paste a URL instead.");
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      {value && <img src={value} alt="" className="w-40 h-28 object-cover rounded-lg border border-white/10" />}
      <div className="flex gap-2">
        <label className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 text-cream/70">
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" className={inputDark} />
      </div>
    </div>
  );
};

const ListEditor = ({ items, onChange, fields, newItem, label }) => {
  const add = () => onChange([...(items || []), { ...newItem }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={i} className="p-3 rounded-lg bg-black/20 border border-white/10 space-y-2 relative">
          <button type="button" onClick={() => remove(i)} className="absolute top-2 right-2 text-cream/65 hover:text-rose">
            <X size={14} />
          </button>
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-cream/65 mb-1">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} rows={3} className={inputDark} />
              ) : (
                <input value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} className={inputDark} />
              )}
            </div>
          ))}
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-dashed border-white/15 rounded-lg text-cream/60 hover:bg-white/5 hover:text-cream">
        <Plus size={12} /> Add {label}
      </button>
    </div>
  );
};

const StringListEditor = ({ items, onChange, label }) => {
  const add = () => onChange([...(items || []), ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  return (
    <div className="space-y-2">
      {(items || []).map((it, i) => (
        <div key={i} className="flex gap-2">
          <input value={it} onChange={(e) => update(i, e.target.value)} className={inputDark} />
          <button type="button" onClick={() => remove(i)} className="text-cream/65 hover:text-rose"><X size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-dashed border-white/15 rounded-lg text-cream/60 hover:bg-white/5 hover:text-cream">
        <Plus size={12} /> Add {label}
      </button>
    </div>
  );
};

const renderField = (f, value, onFieldChange) => {
  // Section header — full-width title + divider, not an input
  if (f.type === "section_header") {
    return (
      <div className="md:col-span-2 mt-2 pt-4 border-t border-white/10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4B896]">{f.label}</p>
      </div>
    );
  }
  const wrap = (node) => (
    <div className={f.full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.24em] text-cream/65 mb-2">{f.label}</label>
      {node}
    </div>
  );
  switch (f.type) {
    case "text":
      return wrap(<input value={value || ""} onChange={(e) => onFieldChange(f.key, e.target.value)} className={inputDark} data-testid={`admin-page-field-${f.key}`} />);
    case "textarea":
      return wrap(<textarea rows={4} value={value || ""} onChange={(e) => onFieldChange(f.key, e.target.value)} className={inputDark} data-testid={`admin-page-field-${f.key}`} />);
    case "image":
      return wrap(<ImageField value={value} onChange={(v) => onFieldChange(f.key, v)} />);
    case "robots":
      return wrap(
        <div className="flex flex-wrap gap-3" data-testid={`admin-page-field-${f.key}`}>
          {[
            { v: "index,follow", l: "Index & Follow", hint: "Show this page in Google search results" },
            { v: "noindex,nofollow", l: "Hide from Google (noindex,nofollow)", hint: "Hide from search engines (e.g. policy duplicates, draft pages)" },
            { v: "noindex,follow", l: "Noindex (but follow links)", hint: "Don't index this page, still pass link signals" },
          ].map((opt) => {
            const current = (value || "index,follow").trim();
            const active = current === opt.v;
            return (
              <button
                type="button"
                key={opt.v}
                onClick={() => onFieldChange(f.key, opt.v)}
                className={`flex-1 min-w-[160px] text-left px-4 py-3 rounded-xl border transition ${active ? "border-[#C9789A] bg-[#C9789A]/15 text-cream" : "border-white/10 bg-black/20 text-cream/75 hover:border-white/20"}`}
                data-testid={`robots-${opt.v.split(',')[0]}`}
              >
                <p className="text-xs uppercase tracking-[0.18em] font-medium">{opt.l}</p>
                <p className="text-[11px] text-cream/65 mt-1 leading-tight">{opt.hint}</p>
              </button>
            );
          })}
        </div>
      );
    case "list:faq":
      return wrap(<ListEditor items={value} onChange={(v) => onFieldChange(f.key, v)} fields={[{ key: "q", label: "Question", type: "text" }, { key: "a", label: "Answer", type: "textarea" }]} newItem={{ q: "", a: "" }} label="FAQ" />);
    case "list:values":
      return wrap(<ListEditor items={value} onChange={(v) => onFieldChange(f.key, v)} fields={[{ key: "icon", label: "Icon (Heart/Leaf/Sparkles/Gem/Star)", type: "text" }, { key: "title", label: "Title", type: "text" }, { key: "body", label: "Body", type: "textarea" }]} newItem={{ icon: "Sparkles", title: "", body: "" }} label="value" />);
    case "list:stats":
      return wrap(<ListEditor items={value} onChange={(v) => onFieldChange(f.key, v)} fields={[{ key: "value", label: "Value (e.g. 50K+)", type: "text" }, { key: "label", label: "Label", type: "text" }]} newItem={{ value: "", label: "" }} label="stat" />);
    case "list:sections":
      return wrap(<ListEditor items={value} onChange={(v) => onFieldChange(f.key, v)} fields={[{ key: "heading", label: "Heading", type: "text" }, { key: "body", label: "Body", type: "textarea" }]} newItem={{ heading: "", body: "" }} label="section" />);
    case "list:subjects":
      return wrap(<StringListEditor items={value} onChange={(v) => onFieldChange(f.key, v)} label="subject" />);
    default:
      return null;
  }
};

const AdminPages = () => {
  const [activeSlug, setActiveSlug] = useState("about");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (slug) => {
    setLoading(true);
    try {
      const r = await api.get(`/pages/${slug}`);
      setData(r.data);
    } catch {
      setData({ slug });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(activeSlug); /* eslint-disable-next-line */ }, [activeSlug]);

  const onFieldChange = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/pages/${activeSlug}`, data);
      toast.success("Page saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const schema = [...(SCHEMAS[activeSlug] || []), ...SEO_FIELDS];

  return (
    <div data-testid="admin-pages">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9789A] mb-2">Content</p>
          <h1 className="font-hero text-3xl text-cream">Pages & policies</h1>
          <p className="text-sm text-cream/75 mt-1 max-w-lg">Manage About, Contact, Terms, Privacy, and Return policy — every field shown here updates the live site immediately.</p>
        </div>
        <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.22em] font-medium text-white" style={{ background: "linear-gradient(135deg, #C9789A, #D4B896)" }} onClick={save} disabled={saving} data-testid="admin-page-save">
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
        {PAGE_TABS.map((t) => (
          <button
            key={t.slug}
            onClick={() => setActiveSlug(t.slug)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.22em] font-medium transition ${
              activeSlug === t.slug ? "bg-white/10 text-cream shadow-[inset_0_0_0_1px_rgba(201,120,154,0.5)]" : "text-cream/75 hover:text-cream hover:bg-white/5"
            }`}
            data-testid={`admin-page-tab-${t.slug}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-cream/75">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 bg-white/[0.08] p-6 rounded-2xl border border-white/10">
          {schema.map((f) => (
            <div key={f.key}>{renderField(f, data[f.key], onFieldChange)}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPages;
